import { useMutation } from '@tanstack/react-query';
import { DatePicker, Form, Input, Space, TableProps } from 'antd';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import {
  Dispatch,
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import {
  GET_STATUS_SALE_PLANNING,
  VARIABLES_GET_STATUS_SALE_PLANNING,
} from 'apis/GraphQL/Queries/codeQuery';
import {
  ICheckCreateSalePlan,
  ICreateSalePlanning,
} from 'apis/GraphQL/Variables/salePlan';
import { SaleLineApi } from 'apis/SaleLineApi';
import SaleLineTeamApis from 'apis/SaleLineTeamApis';
import SalePlanApi from 'apis/SalePlanApis';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { EditGrayIcon, EditIcon, User3Icon } from 'assets/svg';
import { SelectForm, Switch } from 'components';
import ButtonAction from 'components/Button/ButtonAction';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { IGetValidRouteById, IRouteInfo } from 'interfaces/SaleLine';
import { IUpdateSalePlanning } from 'interfaces/SalePlanning';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import {
  IFilterTeamSaleCb,
  IGetAllRouteByTeamSaleInPlant,
  IGetAllTeamSaleByPlant,
  IGetValidTeamSaleById,
} from 'interfaces/TeamSale/TeamSale';
import Content from 'layouts/Content';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import {
  formatCurrency,
  formatDate,
  generateUUID,
  toastError,
  toastSuccess,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import convertUserTeamSalesToCbb from 'utils/convertUserTeamSalesToCbb';
import { HocChangePagination } from 'utils/HocChangePagination';
import DrawerSalePlan from './DrawerSalePlan';

const DaHuyStatus = 'Đã Hủy';
const MoiStatus = 'Mới';

interface IProps extends React.PropsWithChildren<TableProps> {
  filterData: any;
  defaultFilter: any;
  optionPlants: any;
  data: any;
  setData: (data: any) => void;
  editIndex: number;
  isAdd: boolean;
  setIsAdd: Dispatch<boolean>;
  setEditIndex: (data: any) => void;
  refetch: (type?: any, headerRow?: any) => void;
  setFilterData: (data: any) => void;
}
const statusParams = {
  query: GET_STATUS_SALE_PLANNING,
  variables: VARIABLES_GET_STATUS_SALE_PLANNING('107'),
};
const { SaveTwoTone, CloseCircleTwoTone } = icons;

const TableSalePlan = memo(
  forwardRef((props: IProps, ref: any) => {
    const navigate = useNavigate();
    const [statuses, setStatuses] = useState<any[]>([]);
    const isFirstRef = useRef(true);
    const [editRecord, setEditRecord] = useState<any>();
    const [teamSaleData, setTeamSaleData] =
      useState<ILazySelectData>(initLazySelectData);
    const [routes, setRoutes] = useState<ILazySelectData>(initLazySelectData);
    const [form] = useForm();
    const { user } = useUser();
    const [startDate, setStartDate] = useState<any>();
    const [endDate, setEndDate] = useState<any>();
    const [routeSelected, setRouteSelected] = useState<any>();
    const [loading, setLoading] = useState(false);

    const {
      filterData,
      optionPlants,
      defaultFilter,
      data,
      setData,
      editIndex,
      isAdd,
      setFilterData,
      setIsAdd,
      setEditIndex,
      refetch,
    } = props;

    const { mutate: getAllStatuses } = useMutation({
      mutationFn: () => axiosGraphQL.post('/graphql', statusParams),
      onSuccess: (data: any) => {
        const items = data.data?.getCode?.items || [];

        setStatuses(
          items.map((item: any) => ({
            label: item.name,
            value: item.id,
          })),
        );
      },
      onError: (error) => {
        console.log(error);
      },
    });
    const { isPending: getRouteLoading, mutate: getRouteCb } = useMutation({
      mutationFn: (params: IGetAllRouteByTeamSaleInPlant) =>
        SaleLineTeamApis.getAllRouteByTeamSaleInPlant(params),
      onSuccess: (res) => {
        const items = res.data?.getTeamSalesRoute.items ?? [];

        setRoutes({
          options: items.map((item: any) => ({
            label: item.routeId,
            value: item.routeId,
            key: item?.id ?? generateUUID(),
            name: item.troute.name,
            numExpectedDays: item.troute.numExpectedDays,
          })),
          totalItems: res.data?.getTeamSalesRoute.pageInfo?.totalItems ?? 0,
          hasNextPage: res.data?.getTeamSalesRoute.pageInfo?.hasNextPage,
        });
      },
      onError: (error) => {
        console.log(error);
      },
    });

    const { isPending: getTeamSaleLoading, mutate: getTeamSales } = useMutation(
      {
        mutationFn: (params: IGetAllTeamSaleByPlant) =>
          SaleLineTeamApis.getAllTeamSaleByPlant(params),
        onSuccess: (res) => {
          if (res?.data?.getTeamSaleInRoute) {
            const items = res.data?.getTeamSaleInRoute?.items ?? [],
              totalItems =
                res.data?.getTeamSaleInRoute?.pageInfo?.totalItems ?? 0;

            setTeamSaleData({
              options: items.map((item: any) => ({
                label: item.tteamSales.subId,
                value: item.teamSalesId,
                name: item.tteamSales.name,
              })),
              totalItems: totalItems,
              hasNextPage: res.data?.getTeamSaleInRoute?.pageInfo?.hasNextPage,
            });
          }
        },
        onError: (error) => {
          console.log(error);
        },
      },
    );

    const handleRouteSelectScrollChange = (params: ILazySelectParams) => {
      const plantId = form.getFieldValue('plantId') || defaultFilter.plantId;

      if (!plantId || !form.getFieldValue('teamSalesId')) return;

      getRouteCb({
        plantId,
        skip: 0,
        take: params.pageSize || 20,
        teamSalesId: form.getFieldValue('teamSalesId'),
        search: params.value,
      });
    };

    const handleTeamSaleScrollChange = (params: ILazySelectParams) => {
      const plantId = form.getFieldValue('plantId') || defaultFilter.plantId;

      if (!plantId) return;

      if (!form.getFieldValue('routeId')) {
        setRouteSelected(undefined);
        setEndDate(undefined);
        form.setFieldValue('implementationEndDate', undefined);
      }

      const teamSaleParams: IFilterTeamSaleCb = {
        ...params,
        plantId,
      };

      getTeamSales({
        skip: 0,
        take: teamSaleParams.pageSize || 20,
        plantId,
        search: isFirstRef.current
          ? data?.items?.[editIndex]?.teamId
          : teamSaleParams.value,
      });

      isFirstRef.current = false;
    };

    const [isOpenDrawer, setIsOpenDrawer] = useState(false);
    const [planingId, setSalePlaningId] = useState<any>();
    const [statusId, setStatusId] = useState<any>();
    const [routeInfo, setRouteInfo] = useState<IRouteInfo>();
    const [implementationDate, setImplementationDate] = useState<
      string | undefined
    >();
    const [teamLine, setTeamLine] = useState<{
      team?: any;
      line?: any;
    }>({});

    const queryParam = useQueryParam(),
      page = parseInt(queryParam.get('page') + '') || 1,
      pageSize =
        parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

    const addRow = () => {
      if (isAdd) return;

      setIsAdd(true);

      if (!(user?.isAdmin || user?.isHO)) return;
      setRoutes(initLazySelectData);
      setTeamSaleData(initLazySelectData);
    };

    const handleChangePlant = () => {
      form.setFieldsValue({
        teamSalesId: undefined,
        routeId: undefined,
        teamName: undefined,
        routeName: undefined,
      });

      setRoutes(initLazySelectData);
      setTeamSaleData(initLazySelectData);
      form.setFieldValue('implementationEndDate', undefined);
      setEndDate(undefined);

      handleTeamSaleScrollChange({
        pageSize: 20,
      });

      if (editIndex >= 0) {
        handleRouteSelectScrollChange({
          pageSize: 20,
        });
      }
    };

    const handleChangeImplementationDate = (value: any) => {
      setStartDate(value);

      if (routeSelected) {
        const date = dayjs(value).add(
          routeSelected.numExpectedDays - 1,
          'days',
        );

        setEndDate(date);
        form.setFieldValue('implementationEndDate', date);
      } else {
        setEndDate(undefined);
        form.setFieldValue('implementationEndDate', undefined);
      }
    };
    const handleChangeImplementationEndDate = (value: any) => {
      setEndDate(value);
    };

    useImperativeHandle(ref, () => ({ handleExportExcel, addRow }));

    useEffect(() => {
      getAllStatuses();
    }, []);

    useEffect(() => {
      if (isAdd) {
        setData((prev: any) => ({
          ...prev,
          items: [
            {
              id: Date.now(),
            },
          ].concat(prev.items),
        }));

        form.resetFields();
      }
    }, [form, isAdd, setData]);

    const handleExportExcel = () => ({
      columns,
      title: 'Kế hoạch - Lập kế hoạch bán hàng',
    });

    const formatValue = (value: string) => {
      return value === '' || value === null || typeof value === 'undefined'
        ? '-'
        : value;
    };

    const handleChangeTeam = (value: any) => {
      form.setFieldValue('implementationEndDate', undefined);
      setEndDate(undefined);

      const teamSale: any = teamSaleData.options.find(
        (item: any) => item.value === value || item.label === value,
      );

      if (teamSale || defaultFilter?.teamSalesId) {
        handleRouteSelectScrollChange({
          pageSize: 20,
        });
      }

      if (!value) setRoutes(initLazySelectData);

      setTeamLine({
        ...teamLine,
        team: teamSale,
      });

      form.setFieldsValue({
        teamName: teamSale
          ? teamSale?.name
          : (value && defaultFilter?.teamSalesName) || undefined,
        routeId: undefined,
        routeName: undefined,
      });
    };

    const handleChangeRoute = (value: any) => {
      const route: any = routes.options.find(
        (item: any) => item.value === value,
      );

      setRouteSelected(route);

      if (!route) {
        form.setFieldValue('implementationEndDate', undefined);
        setEndDate(undefined);
      } else if (startDate) {
        const date = dayjs(startDate).add(route.numExpectedDays - 1, 'days');

        setEndDate(date);
        form.setFieldValue('implementationEndDate', date);
      }

      setTeamLine({
        ...teamLine,
        line: route,
      });

      form.setFieldsValue({
        routeName: route ? route.name : undefined,
      });
    };

    const columns: ColumnsTypeCustom = [
      {
        title: 'STT',
        align: 'center',
        width: 50,
        render: (_, __, index) => index + 1 + pageSize * (page - 1),
      },
      {
        title: 'Ngày bắt đầu',
        dataIndex: 'implementationDate', //chưa có
        width: 130,
        render: (value: any, _: any, index: number) => {
          if (!isAdd || index || value)
            return value ? formatDate(value, 'DD/MM/YYYY') : '-';

          return (
            <Form.Item
              name={'implementationDate'}
              // rules={[{ required: true, message: 'Chọn ngày thực hiện' }]}
              className="m-0"
              // initialValue={dayjs()}
            >
              <DatePicker
                minDate={dayjs()}
                placeholder="Chọn ngày"
                format="DD/MM/YYYY"
                onChange={handleChangeImplementationDate}
                maxDate={endDate}
              />
            </Form.Item>
          );
        },
        type: 'date',
        formatDate: 'DD/MM/YYYY',
      },
      {
        title: 'Ngày kết thúc',
        dataIndex: 'implementationEndDate', //chưa có
        width: 130,
        render: (value, _, index) => {
          const isRowAdd = isAdd && !index;

          if (index === editIndex || isRowAdd)
            return (
              <Form.Item name={'implementationEndDate'} className="m-0">
                <DatePicker
                  disabled={!routeSelected || !startDate}
                  minDate={startDate || dayjs()}
                  // maxDate={
                  //   routeSelected && startDate
                  //     ? dayjs(startDate).add(
                  //         Number(routeSelected.numExpectedDays) - 1,
                  //         'days',
                  //       )
                  //     : undefined
                  // }
                  placeholder="Chọn ngày"
                  format="DD/MM/YYYY"
                  onChange={handleChangeImplementationEndDate}
                />
              </Form.Item>
            );

          return value ? formatDate(value, 'DD/MM/YYYY') : '-';
        },
        type: 'date',
        formatDate: 'DD/MM/YYYY',
      },
      {
        title: 'Chi nhánh',
        align: 'center',
        width: 110,
        dataIndex: 'plantId',
        render: (value, _: any, index: number) => {
          const isRowAdd = isAdd && !index;

          if (isRowAdd) {
            return (
              <Form.Item
                name={'plantId'}
                initialValue={defaultFilter?.plantId}
                rules={[{ required: true, message: 'Chọn chi nhánh' }]}
                className="m-0"
              >
                <SelectForm
                  size="middle"
                  allowClear={false}
                  options={optionPlants?.items || []}
                  onChange={handleChangePlant}
                  disabled={!(user?.isAdmin || user?.isHO)}
                  placeholder="Chọn chi nhánh"
                  showSearch
                />
                {/* <Input readOnly disabled /> */}
              </Form.Item>
            );
          }

          return formatValue(value);
        },
      },
      {
        title: 'Mã đội',
        dataIndex: 'teamId',
        width: 140,
        render: (value: any, record, index) => {
          const isRowAdd = isAdd && !index;

          return index === editIndex || isRowAdd ? (
            <Form.Item
              name={'teamSalesId'}
              className="m-0"
              initialValue={isRowAdd ? undefined : record?.teamSalesId}
            >
              {user?.isSale ? (
                <SelectForm
                  size="middle"
                  options={convertUserTeamSalesToCbb(user, true)}
                  allowClear
                  onChange={handleChangeTeam}
                  placeholder="Chọn mã đội"
                  showSearch
                />
              ) : (
                <LazySelectForm
                  hasNextPage={teamSaleData.hasNextPage}
                  customeSize={20}
                  size="middle"
                  allowClear
                  onChange={handleChangeTeam}
                  data={teamSaleData}
                  placeholder="Chọn mã đội"
                  loading={getTeamSaleLoading}
                  onSelectScrollChange={handleTeamSaleScrollChange}
                  showSearch
                  // disabled={!teamSaleData.options.length}
                />
              )}
            </Form.Item>
          ) : (
            formatValue(value)
          );
        },
      },
      {
        title: 'Mã tuyến',
        dataIndex: 'routeId',
        width: 140,
        render: (value: any, _, index) => {
          const isRowAdd = isAdd && !index;

          return index === editIndex || isRowAdd ? (
            <Form.Item
              name={'routeId'}
              className="m-0"
              initialValue={isRowAdd ? undefined : value}
            >
              {/* <SelectForm
                size="middle"
                options={convertToLabelValueCbb({
                  data: saleLineFilter,
                })}
                allowClear
                onChange={handleChangeRoute}
                placeholder="Chọn mã tuyến"
                showSearch
              /> */}
              <LazySelectForm
                hasNextPage={routes.hasNextPage}
                customeSize={20}
                size="middle"
                allowClear
                onChange={handleChangeRoute}
                data={routes}
                placeholder="Chọn mã tuyến"
                loading={getRouteLoading}
                onSelectScrollChange={handleRouteSelectScrollChange}
                showSearch
                // disabled={!routes.options.length}
              />
            </Form.Item>
          ) : (
            formatValue(value)
          );
        },
      },
      {
        title: 'Tên tuyến',
        dataIndex: 'routeName',
        width: 180,
        render: (value: any, _, index) => {
          const isRowAdd = isAdd && !index;

          return index === editIndex || isRowAdd ? (
            <Form.Item
              name={'routeName'}
              className="m-0"
              initialValue={isRowAdd ? undefined : value}
            >
              <Input readOnly disabled />
            </Form.Item>
          ) : (
            formatValue(value)
          );
        },
      },
      {
        title: 'Tên đội',
        dataIndex: 'teamName',
        width: 180,
        render: (value: any, _, index) => {
          const isRowAdd = isAdd && !index;

          return index === editIndex || isRowAdd ? (
            <Form.Item
              name={'teamName'}
              className="m-0"
              initialValue={isRowAdd ? undefined : value}
            >
              <Input readOnly disabled />
            </Form.Item>
          ) : (
            formatValue(value)
          );
        },
      },
      {
        type: 'number',
        currency: 'vi-VN',
        align: 'right',
        title: 'Doanh thu chuyến dự kiến',
        dataIndex: 'expectedRevenue',
        width: 200,
        render: (value: any, _: any, index: number) => {
          // const isRowAdd = isAdd && !index;

          // if (isRowAdd)
          //   return (
          //     <Form.Item
          //       name={'expectedRevenue'}
          //       initialValue={isRowAdd ? undefined : value}
          //       // rules={[
          //       //   { required: true, message: 'Nhập doanh thu chuyến dự kiến' },
          //       //   {
          //       //     validator: (_: any, value: number) => {
          //       //       if (value <= 0) {
          //       //         return Promise.reject(
          //       //           'Doanh thu chuyến dự kiến phải lớn hơn 0',
          //       //         );
          //       //       }
          //       //       return Promise.resolve();
          //       //     },
          //       //   },
          //       // ]}
          //       className="m-0 w-full"
          //     >
          //       <InputNumber
          //         className="w-full"
          //         placeholder="Nhập doanh thu chuyến dự kiến"
          //         formatter={(value) => {
          //           return value ? formatNumber(value) : '';
          //         }}
          //         parser={(value: any) => {
          //           return parseFloat(
          //             value.replace(/\./g, '')?.replace(',', '.'),
          //           );
          //         }}
          //         onBlur={(value: any) => {
          //           if (
          //             value.target.value === undefined ||
          //             value.target.value == ''
          //           ) {
          //             form.setFieldsValue({ netRevenuePlanAmt: undefined });
          //           }
          //         }}
          //       />
          //     </Form.Item>
          //   );

          return Number.isInteger(value)
            ? formatCurrency({
                value,
                round: true,
              })
            : '-';
        },
      },
      // {
      //   type: 'number',
      //   align: 'right',
      //   currency: 'vi-VN',
      //   title: 'Doanh thu thực tế',
      //   dataIndex: 'actualRevenue',
      //   width: 190,
      //   render: (value: any) => {
      //     return value
      //       ? formatCurrency({
      //           value,
      //           round: true,
      //         })
      //       : '-';
      //   },
      // },
      {
        title: 'Tiền tệ',
        width: 90,
        dataIndex: 'unitMoney',
        render: (value: any) => {
          return value || 'triệu VND';
        },
      },
      {
        title: 'Năng lực',
        width: 150,
        dataIndex: 'teamCapacity',
        render: (value: any) => {
          return value || '-';
        },
      },
      {
        title: 'Trạng thái',
        width: 160,
        dataIndex: 'statusName',
        render: (value: any) => {
          return value || '-';
        },
      },
      {
        title: 'Người tạo',
        width: 160,
        dataIndex: 'createName',
      },
      {
        // align: 'center',
        title: 'Ngày tạo',
        dataIndex: 'createDate',
        width: 170,
        type: 'date',
        render: (value: any) =>
          value && dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
      },
      {
        title: 'Người cập nhật',
        width: 160,
        dataIndex: 'modifyName',
      },
      {
        // align: 'center',
        title: 'Ngày cập nhật',
        dataIndex: 'modifyDate',
        width: 170,
        type: 'date',
        render: (value: any) =>
          value && dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
      },
      {
        title: 'Thao tác',
        fixed: 'right',
        width: 200,
        align: 'center',
        render: (_: any, record: any, index: number) => {
          const isRowAdd = isAdd && !index;
          const isDisableEdit =
            isAdd || editIndex !== -1 || record?.statusId !== 'A107_02';

          return editIndex == index || isRowAdd ? (
            <Space size={'middle'} className="text-lg">
              <ButtonAction tooltip="Lưu" onClick={() => handleSave(record)}>
                <SaveTwoTone style={{ fontSize: 20 }} />
              </ButtonAction>
              <ButtonAction tooltip="Hủy" onClick={handleCancel}>
                <CloseCircleTwoTone style={{ fontSize: 20 }} />
              </ButtonAction>
            </Space>
          ) : (
            <Space size={'middle'} className="text-lg ">
              {/* <ButtonAction tooltip="Xem chi tiết">
                <EyeIcon />
              </ButtonAction> */}
              <ButtonAction
                onClick={() => {
                  setSalePlaningId(record?.id);
                  setStatusId(record?.statusId);
                  setIsOpenDrawer(!isOpenDrawer);
                  setRouteInfo({
                    id: record?.routeId,
                    cityId: record?.cityIds || [],
                  });
                  setImplementationDate(record?.implementationDate);
                }}
                tooltip="Khách hàng"
              >
                <User3Icon />
              </ButtonAction>
              {/* check role */}
              <ButtonAction
                tooltip="Chỉnh sửa"
                onClick={() => handleEdit(index)}
                disabled={isDisableEdit}
              >
                {!isDisableEdit ? <EditIcon /> : <EditGrayIcon />}
              </ButtonAction>
              <Switch
                disabled={
                  ![DaHuyStatus, MoiStatus].includes(record?.statusName) ||
                  isAdd
                } // Trạng thái: Mới và Đã huỷ
                value={record?.statusName !== DaHuyStatus}
                onChange={() => {
                  handleSwitch(record);
                }}
              />
            </Space>
          );
        },
      },
    ];

    const salePlanUpdateMutation = useMutation({
        mutationFn: (params: IUpdateSalePlanning) =>
          SalePlanApi.updateSalePlanning(params),
        onSuccess: (res) => {
          if (res?.data?.updateSalesPlanning) {
            refetch();
            setEditIndex(-1);
            setEditRecord(null);
            setRouteSelected(undefined);
            setEndDate(undefined);
            setStartDate(undefined);
            form.resetFields();
            toastSuccess('Cập nhật kế hoạch bán hàng thành công');
          } else {
            toastError('Cập nhật kế hoạch bán hàng thất bại');
          }

          setLoading(false);
        },
        onError: (error) => {
          console.log(error);
          toastError('Cập nhật kế hoạch bán hàng thất bại');
          setLoading(false);
        },
      }),
      salePlanUpdateStatusMutation = useMutation({
        mutationFn: (params: any) => SalePlanApi.updateSalePlanning(params),
        onSuccess: (res) => {
          if (res.data?.updateSalesPlanning) {
            refetch();
            toastSuccess('Cập nhật trạng thái thành công');
          }
        },
        onError: (error) => {
          console.log(error);
          toastError('Cập nhật trạng thái thất bại');
        },
      });
    const { mutate } = useMutation({
      mutationFn: (params: ICreateSalePlanning) =>
        SalePlanApi.createSalePlanning(params),
      onSuccess: (res) => {
        if (res?.data?.createSalesPlanning) {
          isAdd || refetch();
          form.resetFields();
          setIsAdd(false);
          navigate(`?page=1&page_size=${pageSize}`);
          setFilterData({
            ...filterData,
          });
          setData({ items: [] });
          setEndDate(undefined);
          setRouteSelected(undefined);
          setStartDate(undefined);
          toastSuccess('Tạo kế hoạch bán hàng thành công');
        } else {
          toastError('Tạo kế hoạch bán hàng thất bại');
        }

        setLoading(false);
      },
      onError: (error) => {
        console.log(error);
        toastError('Tạo kế hoạch bán hàng thất bại');
        setLoading(false);
      },
    });
    // const { mutateAsync: checkExistSalePlan } = useMutation({
    //   mutationFn: (params: ICheckExistSalePlan) =>
    //     SalePlanApi.checkExistSalePlan(params),
    // });
    const { mutateAsync: getValidTeamSaleById } = useMutation({
      mutationFn: (params: IGetValidTeamSaleById) =>
        SaleTeamApi.getValidTeamSaleById(params),
    });
    const { mutateAsync: getValidRouteById } = useMutation({
      mutationFn: (params: IGetValidRouteById) =>
        SaleLineApi.getValidRouteById(params),
    });
    // const { mutateAsync: checkValidDateRoute } = useMutation({
    //   mutationFn: (params: ICheckValidDateRoute) =>
    //     SaleLineApi.checkValidDateRoute(params),
    // });
    // const { mutateAsync: checkExistSaleTeamRoute } = useMutation({
    //   mutationFn: (params: ICheckExistSaleTeamRoute) =>
    //     SaleLineTeamApis.checkExistSaleTeamRoute(params),
    // });
    const { mutateAsync: checkCreateSalePlanning } = useMutation({
      mutationFn: (params: ICheckCreateSalePlan) =>
        SalePlanApi.checkCreateSalePlan(params),
    });

    useEffect(() => {
      setRoutes(initLazySelectData);

      if (editIndex === -1 || !isAdd) setLoading(false);
    }, [editIndex, isAdd]);

    const validateFields = () => {
      const values = form.getFieldsValue();

      if (isAdd && !values.implementationDate)
        return toastError('Vui lòng chọn ngày thực hiện');

      if (isAdd && !values.plantId)
        return toastError('Vui lòng chọn chi nhánh');

      if (!values.teamSalesId)
        return toastError('Vui lòng chọn mã đội bán hàng');

      if (!values.routeId) return toastError('Vui lòng chọn mã tuyến bán hàng');

      if (!values.implementationEndDate)
        return toastError('Vui lòng chọn ngày kết thúc');

      if (isAdd) {
        // if (values.expectedRevenue === undefined)
        //   return toastError('Vui lòng nhập doanh thu chuyến dự kiến');

        // if (values.expectedRevenue <= 0)
        //   return toastError('Doanh thu chuyến dự kiến phải lớn hơn 0');

        if (!values.plantId) return toastError('Vui lòng chọn chi nhánh');
      }

      return true;
    };

    function handleEdit(index: number) {
      form.resetFields();
      setEditIndex(index);
      setEditRecord({ ...data.items[index] });
      isFirstRef.current = true;

      const salePlan = data.items[index];

      if (salePlan) {
        setStartDate(dayjs(salePlan?.implementationDate));
        setEndDate(dayjs(salePlan?.implementationEndDate));
        setRouteSelected({
          label: salePlan?.routeId,
          value: salePlan?.routeId,
          name: salePlan?.routeName,
          numExpectedDays: salePlan?.numExpectedDays ?? 1,
        });
        form.setFieldValue(
          'implementationEndDate',
          dayjs(salePlan?.implementationEndDate),
        );
      }

      form.setFieldsValue({
        plantId: data.items[index]?.plantId,
      });
    }

    const handleSave = async (record: any) => {
      if (loading) return;
      setLoading(true);

      if (typeof validateFields() !== 'boolean') return setLoading(false);

      const newData = { ...record, ...form.getFieldsValue() };
      const teamSalesId = newData?.teamSalesId;
      const implementationDate =
        typeof newData?.implementationDate === 'string'
          ? dayjs(newData?.implementationDate)
          : newData?.implementationDate;
      const formattedDate = implementationDate.format('YYYY-MM-DD 00:00:00');

      const implementationEndDate =
        typeof newData?.implementationEndDate === 'string'
          ? dayjs(newData?.implementationEndDate)
          : newData?.implementationEndDate;
      const formattedEndDate = implementationEndDate.format(
        'YYYY-MM-DD 00:00:00',
      );

      if (isAdd) {
        const subId = user?.isSale
          ? convertUserTeamSalesToCbb(user, true)?.[0]?.label
          : teamSaleData.options.find((item: any) => item.value === teamSalesId)
              ?.label;

        const plantId = newData?.plantId;
        const routeId = newData?.routeId;

        const [
          // getSalePlan,
          getValidTeamSale,
          getValidRoute,
          // validDateRoute,
          // saleTeamRoute,
          // saleTeamRouteUseYn,
          checkCreate,
        ] = await Promise.all([
          // checkExistSalePlan({
          //   implementationDate: date,
          //   plantId,
          //   teamSalesId,
          // }),
          getValidTeamSaleById({
            teamSaleId: teamSalesId,
          }),
          getValidRouteById({
            routeId,
          }),
          // checkValidDateRoute({
          //   routeId: routeId,
          //   validToDate: date,
          // }),
          // checkExistSaleTeamRoute({
          //   teamSalesId,
          //   routeId,
          // }),
          // checkExistSaleTeamRoute({
          //   teamSalesId,
          //   routeId,
          //   useYn: true,
          // }),
          checkCreateSalePlanning({
            routeId,
            implementationDate: formattedDate,
            implementationEndDate: formattedEndDate,
            teamSalesId: teamSalesId,
            plantId: plantId,
          }),
        ]);

        // if (getSalePlan.data?.getSalesPlanning?.items?.length)
        //   return toastError(
        //     'Tạo kế hoạch bán hàng thất bại. Kế hoạch bán hàng đã tồn tại.',
        //   );

        if (!getValidTeamSale.data?.getTeamSales?.items?.length) {
          toastError(
            `Đã có lỗi xảy ra. Đội ${subId} không hoạt động không thể lập kế hoạch bán hàng`,
          );

          return setLoading(false);
        }

        if (
          !getValidRoute.data?.getRouteM?.items?.length
          // ||
          // !validDateRoute.data?.getRouteM?.items?.length
        ) {
          toastError(
            `Đã có lỗi xảy ra. Tuyến ${routeId} không hoạt động không thể lập kế hoạch bán hàng `,
          );

          return setLoading(false);
        }

        // if (!saleTeamRoute.data?.getTeamSalesRoute?.items?.length)
        //   return toastError(
        //     `Đã có lỗi xảy ra. Tuyến - Đội bán hàng không tồn tại`,
        //   );

        // if (!saleTeamRouteUseYn.data?.getTeamSalesRoute?.items?.length)
        //   return toastError(
        //     `Đã có lỗi xảy ra. Tuyến - Đội bán hàng không hoạt động`,
        //   );

        if (checkCreate.data?.checkSalePlanningCreate?.status !== 200) {
          const message = checkCreate.data?.checkSalePlanningCreate?.message;

          toastError(message);

          return setLoading(false);
        }

        const name = `${formattedDate}-${plantId}-${routeId}-${teamSalesId}`;

        mutate({
          name,
          plantId: plantId,
          expectedRevenue: String(newData?.expectedRevenue ?? 0),
          implementationDate: formattedDate,
          implementationEndDate: formattedEndDate,
          teamSalesId: teamSalesId,
          routeId: routeId,
          unitMoney: 'triệu VND',
        });

        return;
      }

      const checkCreate = await checkCreateSalePlanning({
        implementationDate: formattedDate,
        implementationEndDate: formattedEndDate,
        teamSalesId: teamSalesId,
        plantId: newData?.plantId,
        salePlanningId: newData?.id,
      });

      if (checkCreate.data?.checkSalePlanningCreate?.status !== 200) {
        const message = checkCreate.data?.checkSalePlanningCreate?.message;

        toastError(message);

        return setLoading(false);
      }

      salePlanUpdateMutation.mutate({
        id: newData?.id,
        name: newData?.name,
        plantId: newData?.plantId,
        expectedRevenue: String(newData?.expectedRevenue ?? 0),
        implementationDate: formattedDate,
        implementationEndDate: formattedEndDate,
        saleEndDate: newData?.saleEndDate,
        saleStartedDate: newData?.saleStartedDate,
        statusId: newData?.statusId,
        modifyId: user!.id,
        teamSalesId: teamSalesId || record?.teamSalesId,
        routeId: newData?.routeId,
        modifyDate: 'now()',
        unitMoney: 'VND',
      });
    };

    const handleCancel = () => {
      setTeamLine({});
      setStartDate(undefined);
      setEndDate(undefined);
      setRouteSelected(undefined);

      if (isAdd) {
        form.resetFields();
        setData({ ...data, items: data.items.slice(1) });
        setIsAdd(false);

        return;
      }

      editIndex == 0 &&
        !data.items[0]?.id &&
        setData({ ...data, items: data.items.slice(1) }); // check
      form.resetFields();
      setEditIndex(-1);
      setEditRecord(null);
    };

    async function handleSwitch(e: any) {
      if (typeof e === 'undefined') return;

      const params: any = {};

      if (e?.statusName === MoiStatus) {
        Object.assign(params, {
          id: e?.id,
          statusId: statuses.find((item) => item.label === DaHuyStatus)?.value,
        });
      } else if (e?.statusName === DaHuyStatus) {
        Object.assign(params, {
          id: e?.id,
          statusId: statuses.find((item) => item.label === MoiStatus)?.value,
        });

        const implementationDate =
          typeof e?.implementationDate === 'string'
            ? dayjs(e?.implementationDate)
            : e?.implementationDate;
        const formattedDate = implementationDate.format('YYYY-MM-DD 00:00:00');

        const implementationEndDate =
          typeof e?.implementationEndDate === 'string'
            ? dayjs(e?.implementationEndDate)
            : e?.implementationEndDate;
        const formattedEndDate = implementationEndDate.format(
          'YYYY-MM-DD 00:00:00',
        );

        const checkCreate = await checkCreateSalePlanning({
          implementationDate: formattedDate,
          implementationEndDate: formattedEndDate,
          teamSalesId: e.teamSalesId,
          plantId: e?.plantId,
          salePlanningId: e?.id,
        });

        if (checkCreate.data?.checkSalePlanningCreate?.status !== 200) {
          const message = checkCreate.data?.checkSalePlanningCreate?.message;

          toastError(message);

          return setLoading(false);
        }
      }

      if (Object.keys(params).length > 0) {
        salePlanUpdateStatusMutation.mutate(params);
      }
    }

    return (
      <Content>
        <Form form={form}>
          <TableComponent
            dataSource={data.items}
            columns={columns}
            pagination={{
              disabled: isAdd || editIndex !== -1,
              current: data.pageInfo?.currentPage,
              pageSize: data.pageInfo?.itemsPerPage + (isAdd ? 1 : 0),
              total: data.pageInfo?.totalItems,
              onChange: HocChangePagination(),
            }}
            loading={
              props.loading || salePlanUpdateMutation.isPending || loading
            }
          />
          <DrawerSalePlan
            refetch={refetch}
            statusId={statusId}
            open={isOpenDrawer}
            setOpen={setIsOpenDrawer}
            salePlaningId={planingId || ''}
            routeInfo={routeInfo}
            implementationDate={implementationDate}
          />
        </Form>
      </Content>
    );
  }),
);

export default TableSalePlan;
